import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box, Paper, Typography, TextField, Switch,
  IconButton, Divider, Chip, Button, Tooltip,
  CircularProgress,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';

// ─────────────────────────────────────────────────────────────────
// DEFINIZIONI DEI CAMPI — funzioni che ricevono t (namespace setup)
// fieldKey = null → toggle dell'intero modulo (es. gomma)
// ─────────────────────────────────────────────────────────────────

const getCornerFieldDefs = (t) => {
  const GEO_SX = [
    { fieldKey: 'convergenzaSX',    moduleKey: 'alignment',       label: t('cc_label_toe') },
    { fieldKey: 'camberSX',         moduleKey: 'alignment',       label: t('cc_label_camber') },
  ];
  const GEO_DX = [
    { fieldKey: 'convergenzaDX',    moduleKey: 'alignment',       label: t('cc_label_toe') },
    { fieldKey: 'camberDX',         moduleKey: 'alignment',       label: t('cc_label_camber') },
  ];
  const CASTER_SX = { fieldKey: 'casterSX', moduleKey: 'alignment', label: t('cc_label_caster') };
  const CASTER_DX = { fieldKey: 'casterDX', moduleKey: 'alignment', label: t('cc_label_caster') };
  const HEIGHT_SX = { fieldKey: 'altezzaTargetSX', moduleKey: 'aero', label: t('cc_label_height') };
  const HEIGHT_DX = { fieldKey: 'altezzaTargetDX', moduleKey: 'aero', label: t('cc_label_height') };
  const SPRINGS_SX = [
    { fieldKey: 'mollaSX',             moduleKey: 'mechanical_grip', label: t('cc_label_spring') },
    { fieldKey: 'rigidezzaBumpstopSX', moduleKey: 'mechanical_grip', label: t('cc_label_bs_rate') },
    { fieldKey: 'distanzaBumpstopSX',  moduleKey: 'mechanical_grip', label: t('cc_label_bs_range') },
  ];
  const SPRINGS_DX = [
    { fieldKey: 'mollaDX',             moduleKey: 'mechanical_grip', label: t('cc_label_spring') },
    { fieldKey: 'rigidezzaBumpstopDX', moduleKey: 'mechanical_grip', label: t('cc_label_bs_rate') },
    { fieldKey: 'distanzaBumpstopDX',  moduleKey: 'mechanical_grip', label: t('cc_label_bs_range') },
  ];
  const DAMPERS_SX = [
    { fieldKey: 'bumpSX',        moduleKey: 'dampers', label: t('cc_label_bump_slow') },
    { fieldKey: 'reboundSX',     moduleKey: 'dampers', label: t('cc_label_rebound_slow') },
    { fieldKey: 'fastBumpSX',    moduleKey: 'dampers', label: t('cc_label_bump_fast') },
    { fieldKey: 'fastReboundSX', moduleKey: 'dampers', label: t('cc_label_rebound_fast') },
  ];
  const DAMPERS_DX = [
    { fieldKey: 'bumpDX',        moduleKey: 'dampers', label: t('cc_label_bump_slow') },
    { fieldKey: 'reboundDX',     moduleKey: 'dampers', label: t('cc_label_rebound_slow') },
    { fieldKey: 'fastBumpDX',    moduleKey: 'dampers', label: t('cc_label_bump_fast') },
    { fieldKey: 'fastReboundDX', moduleKey: 'dampers', label: t('cc_label_rebound_fast') },
  ];
  const TYRE_MODULE = { fieldKey: null, moduleKey: 'strategy_tires', label: t('cc_label_p_cold') + ' & ' + t('cc_label_tyre_code') };
  const secGeo  = t('cc_section_geometry');
  const secSpr  = t('cc_section_springs');
  const secDamp = t('section_dampers');
  const secTyre = t('cc_section_tyre');
  const secPesi = t('cc_corner_weight');

  return {
    FL: [
      { section: secGeo,  fields: [...GEO_SX, CASTER_SX, HEIGHT_SX] },
      { section: secSpr,  fields: SPRINGS_SX },
      { section: secDamp, fields: DAMPERS_SX },
      { section: secTyre, fields: [TYRE_MODULE] },
      { section: secPesi, fields: [{ fieldKey: 'cornerFL', moduleKey: 'weights', label: 'FL' }] },
    ],
    FR: [
      { section: secGeo,  fields: [...GEO_DX, CASTER_DX, HEIGHT_DX] },
      { section: secSpr,  fields: SPRINGS_DX },
      { section: secDamp, fields: DAMPERS_DX },
      { section: secTyre, fields: [TYRE_MODULE] },
      { section: secPesi, fields: [{ fieldKey: 'cornerFR', moduleKey: 'weights', label: 'FR' }] },
    ],
    RL: [
      { section: secGeo,  fields: GEO_SX },
      { section: secSpr,  fields: SPRINGS_SX },
      { section: secDamp, fields: DAMPERS_SX },
      { section: secTyre, fields: [TYRE_MODULE] },
      { section: secPesi, fields: [{ fieldKey: 'cornerRL', moduleKey: 'weights', label: 'RL' }] },
    ],
    RR: [
      { section: secGeo,  fields: GEO_DX },
      { section: secSpr,  fields: SPRINGS_DX },
      { section: secDamp, fields: DAMPERS_DX },
      { section: secTyre, fields: [TYRE_MODULE] },
      { section: secPesi, fields: [{ fieldKey: 'cornerRR', moduleKey: 'weights', label: 'RR' }] },
    ],
  };
};

const getChassisSection = (t) => [
  {
    sectionKey: 'chassis', label: t('cc_tab_chassis'), groups: [
      { moduleKey: 'mechanical_grip', label: t('cc_group_arb') + ' & ' + t('cc_group_diff'), fields: [
        { fieldKey: 'barra',              label: t('cc_group_arb') },
        { fieldKey: 'precarico',          label: t('field_diff_preload') },
        { fieldKey: 'bloccaggio',         label: t('field_diff_lock') },
        { fieldKey: 'bilanciamentoFreni', label: t('field_brake_bias') },
        { fieldKey: 'potenzaFreni',       label: t('field_brake_power') },
        { fieldKey: 'rapportoSterzo',     label: t('field_steering_ratio') },
      ]},
      { moduleKey: 'aero', label: t('section_aero'), fields: [
        { fieldKey: 'alaPosteriore',           label: t('field_wing_rear') },
        { fieldKey: 'splitterAnteriore',       label: t('field_splitter') },
        { fieldKey: 'condottiFrenoAnteriori',  label: t('field_brake_ducts_front') },
        { fieldKey: 'condottiFrenoPosteriori', label: t('field_brake_ducts_rear') },
      ]},
      { moduleKey: 'electronics', label: t('section_electronics'), fields: [
        { fieldKey: 'tc1',      label: t('field_electronics_tc1') },
        { fieldKey: 'tc2',      label: t('field_electronics_tc2') },
        { fieldKey: 'abs',      label: t('field_electronics_abs') },
        { fieldKey: 'mappaECU', label: t('field_electronics_ecu') },
      ]},
    ],
  },
  {
    sectionKey: 'weights', label: t('cc_tab_weights'), groups: [
      { moduleKey: 'weights', label: t('cc_group_weights_base'), fields: [
        { fieldKey: 'dryWeight',    label: t('field_dry_weight') },
        { fieldKey: 'fuelLiters',   label: t('field_fuel_liters') },
        { fieldKey: 'driverWeight', label: t('field_driver_weight') },
        { fieldKey: 'minRegWeight', label: t('field_min_reg_weight') },
        { fieldKey: 'ballast',      label: t('field_ballast_applied') },
      ]},
    ],
  },
  {
    sectionKey: 'strategy', label: t('cc_tab_strategy'), groups: [
      { moduleKey: 'strategy_tires', label: t('cc_group_fuel'), fields: [
        { fieldKey: 'carburanteIniziale',       label: t('field_strategy_fuel_start') },
        { fieldKey: 'strategiaPit',             label: t('field_strategy_pit_plan') },
        { fieldKey: 'pastiglieFrenoAnteriori',  label: t('field_strategy_brakes_front') },
        { fieldKey: 'pastiglieFrenoPosteriori', label: t('field_strategy_brakes_rear') },
      ]},
    ],
  },
  {
    sectionKey: 'feedback', label: t('cc_tab_notes'), groups: [
      { moduleKey: 'feedback', label: t('section_feedback'), fields: [
        { fieldKey: 'bestLap',        label: t('field_best_lap') },
        { fieldKey: 'listaTempi',     label: t('field_times_list') },
        { fieldKey: 'noteTecnico',    label: t('field_tech_notes') },
        { fieldKey: 'feedbackPilota', label: t('field_driver_feedback') },
        { fieldKey: 'linkTelemetria', label: t('field_telemetry_link') },
        { fieldKey: 'linkVideo',      label: t('field_video_link') },
      ]},
    ],
  },
];

// ─────────────────────────────────────────────────────────────────
// COMPONENTI UI INTERNI
// ─────────────────────────────────────────────────────────────────

const SectionLabel = ({ label }) => (
  <Typography variant="caption" sx={{
    display: 'block', pt: 1, pb: '3px',
    fontWeight: 700, letterSpacing: '0.09em',
    textTransform: 'uppercase', color: 'text.disabled',
    borderBottom: '1px solid', borderColor: 'divider', mb: '2px',
  }}>
    {label}
  </Typography>
);

const ToggleRow = ({ label, checked, onChange, dimmed }) => (
  <Box sx={{
    display: 'flex', alignItems: 'center',
    justifyContent: 'space-between',
    py: '2px',
    borderBottom: '1px solid', borderColor: 'divider',
    '&:last-of-type': { borderBottom: 'none' },
    opacity: dimmed ? 0.4 : 1,
  }}>
    <Typography
      variant="caption"
      sx={{ color: checked ? 'text.primary' : 'text.secondary', fontSize: '11.5px' }}
    >
      {label}
    </Typography>
    <Switch
      size="small"
      checked={checked}
      onChange={onChange}
      sx={{
        '& .MuiSwitch-switchBase.Mui-checked': { color: 'text.primary' },
        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: 'text.secondary' },
      }}
    />
  </Box>
);

// ─────────────────────────────────────────────────────────────────
// CORNER TEMPLATE CARD
// ─────────────────────────────────────────────────────────────────

const CornerTemplateCard = ({ tag, modulesConfig, activeModules, onToggleField, onToggleModule, onAddCustomField, onRemoveCustomField }) => {
  const { t: tSetup } = useTranslation('setup');
  const [newFieldLabel, setNewFieldLabel] = useState('');
  const [newFieldType, setNewFieldType] = useState('text');

  const cornerLabel = {
    FL: tSetup('quadrant_fl', { defaultValue: 'Ant. Sinistra' }),
    FR: tSetup('quadrant_fr', { defaultValue: 'Ant. Destra' }),
    RL: tSetup('quadrant_rl', { defaultValue: 'Post. Sinistra' }),
    RR: tSetup('quadrant_rr', { defaultValue: 'Post. Destra' }),
  }[tag];
  const cornerFieldDefs = useMemo(() => getCornerFieldDefs(tSetup), [tSetup]);
  const sections = cornerFieldDefs[tag] || [];

  const isFieldOn = (moduleKey, fieldKey) => {
    if (!activeModules.includes(moduleKey)) return false;
    if (fieldKey === null) return true; // module-level toggle
    return !(modulesConfig[moduleKey]?.hiddenFields || []).includes(fieldKey);
  };

  const handleToggle = (moduleKey, fieldKey) => {
    if (fieldKey === null) {
      onToggleModule(moduleKey);
    } else {
      onToggleField(moduleKey, fieldKey);
    }
  };

  const customFields = (modulesConfig['alignment']?.customFields || []).filter(f => f.position === tag);

  const handleAddCustom = () => {
    if (!newFieldLabel.trim()) return;
    onAddCustomField(tag, newFieldLabel.trim(), newFieldType);
    setNewFieldLabel('');
    setNewFieldType('text');
  };

  return (
    <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
      <Box sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        px: 1.5, py: 1,
        backgroundColor: 'background.paper', borderBottom: '1px solid', borderColor: 'divider',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
          <Typography fontWeight={800} fontSize={17} lineHeight={1}>{tag}</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            {cornerLabel}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ px: 1.5, pb: 1 }}>
        {sections.map(({ section, fields }) => (
          <React.Fragment key={section}>
            <SectionLabel label={section} />
            {fields.map((f) => (
              <ToggleRow
                key={f.fieldKey ?? `module-${f.moduleKey}`}
                label={f.label}
                checked={isFieldOn(f.moduleKey, f.fieldKey)}
                onChange={() => handleToggle(f.moduleKey, f.fieldKey)}
              />
            ))}
          </React.Fragment>
        ))}

        {/* Custom fields */}
        <SectionLabel label={
          <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            Custom
            <Chip label="Template" size="small" sx={{ height: 14, fontSize: 9, fontWeight: 700, '& .MuiChip-label': { px: 0.6 } }} color="success" />
          </Box>
        } />
        {customFields.map(cf => (
          <Box key={cf.key} sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            py: '2px', borderBottom: '1px solid', borderColor: 'divider',
          }}>
            <Typography variant="caption" color="text.primary" sx={{ fontSize: '11.5px' }}>
              {cf.label}
              <Typography component="span" variant="caption" color="text.disabled" sx={{ ml: 0.5 }}>
                ({cf.type})
              </Typography>
            </Typography>
            <Tooltip title="Rimuovi campo">
              <IconButton size="small" onClick={() => onRemoveCustomField('alignment', cf.key)} sx={{ p: 0.25 }}>
                <DeleteIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          </Box>
        ))}

        {/* Add custom field */}
        <Box sx={{ display: 'flex', gap: 0.5, mt: 0.75, alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="Nuovo campo..."
            value={newFieldLabel}
            onChange={(e) => setNewFieldLabel(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleAddCustom()}
            sx={{ flex: 1, '& .MuiInputBase-input': { fontSize: 11, py: 0.5 } }}
          />
          <TextField
            select
            size="small"
            value={newFieldType}
            onChange={(e) => setNewFieldType(e.target.value)}
            SelectProps={{ native: true }}
            sx={{ width: 70, '& .MuiInputBase-input': { fontSize: 11, py: 0.5 } }}
          >
            <option value="text">Testo</option>
            <option value="number">Num.</option>
          </TextField>
          <IconButton size="small" onClick={handleAddCustom} disabled={!newFieldLabel.trim()}
            sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 0.5 }}>
            <AddIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Box>
      </Box>
    </Paper>
  );
};

// ─────────────────────────────────────────────────────────────────
// CHASSIS TEMPLATE PANEL
// ─────────────────────────────────────────────────────────────────

const ChassisTemplatePanel = ({ modulesConfig, activeModules, onToggleField, onToggleModule, onAddCustomField, onRemoveCustomField }) => {
  const { t: tSetup } = useTranslation('setup');
  const chassisSections = useMemo(() => getChassisSection(tSetup), [tSetup]);
  const [newFieldLabel, setNewFieldLabel] = useState({});
  const [newFieldType, setNewFieldType] = useState({});

  const isFieldOn = (moduleKey, fieldKey) => {
    if (!activeModules.includes(moduleKey)) return false;
    return !(modulesConfig[moduleKey]?.hiddenFields || []).includes(fieldKey);
  };

  return (
    <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2, mt: 1.5 }}>
      {chassisSections.map(({ sectionKey, label, groups }) => (
        <Box key={sectionKey} sx={{ mb: 2 }}>
          {/* Section divider */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <Typography variant="caption" sx={{ fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'text.disabled', fontSize: 9, flexShrink: 0 }}>
              {label}
            </Typography>
            <Divider sx={{ flex: 1 }} />
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(auto-fill, minmax(160px, 1fr))' }, gap: 2 }}>
            {groups.map(({ moduleKey, label: groupLabel, fields }) => {
              const moduleActive = activeModules.includes(moduleKey);
              const sectionCustomFields = (modulesConfig[moduleKey]?.customFields || []).filter(f => !f.position || f.position === 'General');
              const cfKey = `${sectionKey}-${moduleKey}`;

              return (
                <Box key={moduleKey}>
                  {/* Group header con module toggle */}
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: '0.09em', textTransform: 'uppercase', color: moduleActive ? 'text.secondary' : 'text.disabled', fontSize: 9, borderBottom: '1px solid', borderColor: 'divider', pb: '2px', flex: 1 }}>
                      {groupLabel}
                    </Typography>
                    <Tooltip title={moduleActive ? 'Disattiva sezione' : 'Attiva sezione'}>
                      <Switch
                        size="small"
                        checked={moduleActive}
                        onChange={() => onToggleModule(moduleKey)}
                        sx={{
                          ml: 0.5,
                          '& .MuiSwitch-switchBase.Mui-checked': { color: 'text.primary' },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: 'text.secondary' },
                        }}
                      />
                    </Tooltip>
                  </Box>

                  {/* Fields */}
                  {fields.map(f => (
                    <ToggleRow
                      key={f.fieldKey}
                      label={f.label}
                      checked={isFieldOn(moduleKey, f.fieldKey)}
                      onChange={() => onToggleField(moduleKey, f.fieldKey)}
                      dimmed={!moduleActive}
                    />
                  ))}

                  {/* Custom fields for this section */}
                  {sectionCustomFields.map(cf => (
                    <Box key={cf.key} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: '2px', borderBottom: '1px solid', borderColor: 'divider' }}>
                      <Typography variant="caption" color="text.primary" sx={{ fontSize: '11.5px' }}>
                        {cf.label} <Typography component="span" variant="caption" color="text.disabled">({cf.type})</Typography>
                      </Typography>
                      <IconButton size="small" onClick={() => onRemoveCustomField(moduleKey, cf.key)} sx={{ p: 0.25 }}>
                        <DeleteIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Box>
                  ))}

                  {/* Add custom field */}
                  {moduleActive && (
                    <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, alignItems: 'center' }}>
                      <TextField
                        size="small"
                        placeholder="Campo..."
                        value={newFieldLabel[cfKey] || ''}
                        onChange={(e) => setNewFieldLabel(p => ({ ...p, [cfKey]: e.target.value }))}
                        sx={{ flex: 1, '& .MuiInputBase-input': { fontSize: 10, py: 0.4 } }}
                      />
                      <TextField
                        select size="small"
                        value={newFieldType[cfKey] || 'text'}
                        onChange={(e) => setNewFieldType(p => ({ ...p, [cfKey]: e.target.value }))}
                        SelectProps={{ native: true }}
                        sx={{ width: 65, '& .MuiInputBase-input': { fontSize: 10, py: 0.4 } }}
                      >
                        <option value="text">Testo</option>
                        <option value="number">Num.</option>
                      </TextField>
                      <IconButton size="small"
                        disabled={!(newFieldLabel[cfKey]?.trim())}
                        onClick={() => {
                          onAddCustomField('General', newFieldLabel[cfKey]?.trim(), newFieldType[cfKey] || 'text', moduleKey);
                          setNewFieldLabel(p => ({ ...p, [cfKey]: '' }));
                        }}
                        sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 0.4 }}
                      >
                        <AddIcon sx={{ fontSize: 13 }} />
                      </IconButton>
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>
      ))}
    </Paper>
  );
};

// ─────────────────────────────────────────────────────────────────
// TEMPLATE EDITOR — COMPONENTE PRINCIPALE
// ─────────────────────────────────────────────────────────────────

// Moduli disponibili (stesso ordine di AVAILABLE_MODULES nel vecchio codice)
const ALL_MODULE_KEYS = ['weights', 'alignment', 'mechanical_grip', 'dampers', 'aero', 'electronics', 'strategy_tires', 'feedback'];

// Default template per nuovi template
const DEFAULT_ACTIVE_MODULES = ['alignment', 'mechanical_grip', 'dampers', 'feedback'];

const oldToNewMapping = { weights: 'weights', front: 'alignment', rear: 'alignment', aero: 'aero', tires: 'strategy_tires', feedback: 'feedback' };

/**
 * Editor car-centric per template setup.
 * Sostituisce l'EditTemplateModal + ModuleConfigDialog del vecchio sistema.
 *
 * Props:
 *   template   – template esistente o null (nuovo)
 *   onSave(templateId, templateName, modules) – stessa firma del vecchio onSave
 *   onCancel() – torna alla lista
 *   t          – useTranslation('setup_template')
 */
const TemplateEditor = ({ template, onSave, onCancel, t: tTemplate }) => {
  const t = tTemplate; // alias per leggibilità
  const { t: tSetup } = useTranslation('setup');
  const [templateName, setTemplateName]     = useState('');
  const [activeModules, setActiveModules]   = useState(DEFAULT_ACTIVE_MODULES);
  const [modulesConfig, setModulesConfig]   = useState({});
  const [isSaving, setIsSaving]             = useState(false);

  // Inizializza stato dall'template esistente (o defaults per nuovo)
  useEffect(() => {
    if (template) {
      setTemplateName(template.templateName || '');
      const config = {};
      const active = [];

      if (template.modules) {
        template.modules.forEach(m => {
          active.push(m.key);
          config[m.key] = m.config || { hiddenFields: [], customFields: [] };
        });
      } else if (template.sections) {
        // Migrazione vecchio formato
        for (const oldKey in template.sections) {
          if (template.sections[oldKey] === true) {
            const newKey = oldToNewMapping[oldKey];
            if (newKey && !active.includes(newKey)) {
              active.push(newKey);
              config[newKey] = { hiddenFields: [], customFields: [] };
            }
          }
        }
      }
      setActiveModules(active);
      setModulesConfig(config);
    } else {
      setTemplateName('');
      setActiveModules(DEFAULT_ACTIVE_MODULES);
      const cfg = {};
      DEFAULT_ACTIVE_MODULES.forEach(k => { cfg[k] = { hiddenFields: [], customFields: [] }; });
      setModulesConfig(cfg);
    }
  }, [template]);

  // ── HELPERS ────────────────────────────────────────────────────

  const ensureModuleConfig = useCallback((moduleKey) => {
    setModulesConfig(prev => ({
      ...prev,
      [moduleKey]: prev[moduleKey] || { hiddenFields: [], customFields: [] },
    }));
  }, []);

  const handleToggleModule = useCallback((moduleKey) => {
    setActiveModules(prev => {
      if (prev.includes(moduleKey)) return prev.filter(k => k !== moduleKey);
      ensureModuleConfig(moduleKey);
      return [...prev, moduleKey];
    });
  }, [ensureModuleConfig]);

  const handleToggleField = useCallback((moduleKey, fieldKey) => {
    // Se il modulo non è attivo, attivarlo prima
    setActiveModules(prev => prev.includes(moduleKey) ? prev : [...prev, moduleKey]);
    ensureModuleConfig(moduleKey);
    setModulesConfig(prev => {
      const cfg = prev[moduleKey] || { hiddenFields: [], customFields: [] };
      const hidden = cfg.hiddenFields || [];
      const newHidden = hidden.includes(fieldKey)
        ? hidden.filter(f => f !== fieldKey)   // rimuovi dall'hidden = abilita
        : [...hidden, fieldKey];               // aggiungi all'hidden = disabilita
      return { ...prev, [moduleKey]: { ...cfg, hiddenFields: newHidden } };
    });
  }, [ensureModuleConfig]);

  const handleAddCustomField = useCallback((position, label, type, moduleKey = 'alignment') => {
    ensureModuleConfig(moduleKey);
    setModulesConfig(prev => {
      const cfg = prev[moduleKey] || { hiddenFields: [], customFields: [] };
      const key = label.toLowerCase().replace(/\s+/g, '_') + '_' + Date.now();
      return {
        ...prev,
        [moduleKey]: {
          ...cfg,
          customFields: [...(cfg.customFields || []), { key, label, type, position }],
        },
      };
    });
  }, [ensureModuleConfig]);

  const handleRemoveCustomField = useCallback((moduleKey, cfKey) => {
    setModulesConfig(prev => {
      const cfg = prev[moduleKey] || { hiddenFields: [], customFields: [] };
      return {
        ...prev,
        [moduleKey]: { ...cfg, customFields: (cfg.customFields || []).filter(f => f.key !== cfKey) },
      };
    });
  }, []);

  // ── SALVATAGGIO ─────────────────────────────────────────────────

  const handleSave = async () => {
    if (!templateName.trim()) {
      alert(t('error_name_module', { defaultValue: 'Inserisci un nome per il template.' }));
      return;
    }
    if (activeModules.length === 0) {
      alert(t('error_name_module', { defaultValue: 'Attiva almeno un modulo.' }));
      return;
    }
    setIsSaving(true);
    try {
      const modulesToSave = ALL_MODULE_KEYS
        .filter(k => activeModules.includes(k))
        .map(k => ({
          key: k,
          title: k, // titolo in inglese, mantenuto per compat
          config: modulesConfig[k] || { hiddenFields: [], customFields: [] },
        }));
      await onSave(template?.id, templateName.trim(), modulesToSave);
    } catch (err) {
      console.error('Error saving template:', err);
      alert(t('save_error', { defaultValue: 'Errore durante il salvataggio.' }));
    } finally {
      setIsSaving(false);
    }
  };

  // ── RENDER ─────────────────────────────────────────────────────

  const cornerProps = (tag) => ({
    tag,
    modulesConfig,
    activeModules,
    onToggleField: handleToggleField,
    onToggleModule: handleToggleModule,
    onAddCustomField: handleAddCustomField,
    onRemoveCustomField: handleRemoveCustomField,
  });

  return (
    <Box>
      {/* Header */}
      <Paper elevation={0} sx={{ p: 2, mb: 2, border: '1px solid', borderColor: 'divider', backgroundColor: 'background.paper' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <IconButton onClick={onCancel} size="small">
            <ArrowBackIcon />
          </IconButton>
          <TextField
            value={templateName}
            onChange={(e) => setTemplateName(e.target.value)}
            placeholder={t('template_name_label', { defaultValue: 'Nome template...' })}
            variant="standard"
            size="small"
            sx={{ flex: 1, minWidth: 200, '& .MuiInputBase-input': { fontSize: 18, fontWeight: 700 } }}
          />
          <Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
            <Button variant="outlined" onClick={onCancel} disabled={isSaving}>
              {t('edit_cancel_button', { defaultValue: 'Annulla' })}
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={isSaving || !templateName.trim()}
              startIcon={isSaving ? <CircularProgress size={16} /> : <SaveIcon />}
            >
              {t('edit_save_button', { defaultValue: 'Salva Template' })}
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Etichette asse — decorative, non tradotte */}
      <Box sx={{ display: { xs: 'none', sm: 'flex' }, justifyContent: 'space-between', px: 0.5, mb: 0.5 }}>
        <Typography variant="caption" sx={{ color: 'text.disabled', letterSpacing: '0.12em', textTransform: 'uppercase', fontSize: 9 }}>← {tSetup('detail_front_title', { defaultValue: 'Anteriore' })}</Typography>
        <Typography variant="caption" sx={{ color: 'text.disabled', letterSpacing: '0.12em', textTransform: 'uppercase', fontSize: 9 }}>↑</Typography>
        <Typography variant="caption" sx={{ color: 'text.disabled', letterSpacing: '0.12em', textTransform: 'uppercase', fontSize: 9 }}>{tSetup('detail_rear_title', { defaultValue: 'Posteriore' })} →</Typography>
      </Box>

      {/* 4 Corner Cards */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5, alignItems: 'start' }}>
        <CornerTemplateCard {...cornerProps('FL')} />
        <CornerTemplateCard {...cornerProps('FR')} />
        <CornerTemplateCard {...cornerProps('RL')} />
        <CornerTemplateCard {...cornerProps('RR')} />
      </Box>

      {/* Chassis bottom panel */}
      <ChassisTemplatePanel
        modulesConfig={modulesConfig}
        activeModules={activeModules}
        onToggleField={handleToggleField}
        onToggleModule={handleToggleModule}
        onAddCustomField={handleAddCustomField}
        onRemoveCustomField={handleRemoveCustomField}
      />
    </Box>
  );
};

export default TemplateEditor;
